// src/stores/subscription.store.ts
import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { useAuthStore } from './auth.store';

export type Plan = 'free' | 'decouverte' | 'pro' | 'enterprise' | 'avance';

export interface Subscription {
  id: string;
  user_id: string;
  plan: Plan;
  analyses_used: number;
  analyses_remaining: number;
  is_active: boolean;
  expires_at: string;
  stripe_subscription_id: string;
  actions_completed: string[];
  created_at: string;
  updated_at: string;
  is_paid_user: boolean;
}

interface SubscriptionState {
  subscription: Subscription | null;
  loading: boolean;
  error: string | null;

  setSubscription: (sub: Subscription | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  fetchSubscription: () => Promise<void>;
  upgradeSubscription: (plan: 'decouverte' | 'avance' | 'pro') => Promise<{ data: Subscription | null; error: any }>;
  addBonusAnalyses: (bonusAnalyses: number, actionId: string) => Promise<{ data: Subscription | null; error: any }>;
  reset: () => void;
}

export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
  subscription: null,
  loading: false,
  error: null,

  setSubscription: (sub) => set({ subscription: sub }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),

  reset: () => set({ subscription: null, loading: false, error: null }),

  fetchSubscription: async () => {
    const { user } = useAuthStore.getState();
    if (!user?.id) {
      // ensure consumers are not stuck
      set({ loading: false });
      return;
    }
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('user_analyses')
        .select('*')
        .eq('user_id', user.id)
        .single();
      if (error) {
        if ((error as any).code === 'PGRST116') {
          const fallback: Subscription = {
            id: `free-${user.id}`,
            user_id: user.id,
            plan: 'free',
            analyses_used: 0,
            analyses_remaining: 5,
            is_active: true,
            expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            stripe_subscription_id: '',
            actions_completed: [],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            is_paid_user: false,
          };
          set({ subscription: fallback });
        } else {
          set({ error: error.message, subscription: null });
        }
      } else {
        // Ensure the subscription data is properly typed and updated
        const subscriptionData = data as Subscription;
        set({ subscription: subscriptionData });
      }
    } catch (err: any) {
      set({ error: err.message || 'Unknown error', subscription: null });
    } finally {
      set({ loading: false });
    }
  },


  upgradeSubscription: async (plan) => {
    const { user } = useAuthStore.getState();
    if (!user?.id) return { data: null, error: 'Not authenticated' };

    try {
      // Fetch current sub to compute new remaining
      const { data: currentSub } = await supabase
        .from('user_analyses')
        .select('*')
        .eq('user_id', user.id)
        .single();

      let analysesToAdd = 0;
      if (plan === 'decouverte') analysesToAdd = 10;
      if (plan === 'avance') analysesToAdd = 30;
      if (plan === 'pro') analysesToAdd = 100;

      const newAnalysesRemaining = (currentSub?.analyses_remaining || 0) + analysesToAdd;
      const expireAt = new Date();
      expireAt.setMonth(expireAt.getMonth() + 1);

      const { data, error } = await supabase
        .from('user_analyses')
        .update({
          plan,
          analyses_remaining: newAnalysesRemaining,
          is_active: true,
          expires_at: expireAt.toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;

      set({ subscription: data as Subscription });
      return { data: data as Subscription, error: null };
    } catch (error) {
      set({ error: (error as any)?.message || 'Upgrade failed' });
      return { data: null, error };
    }
  },

  addBonusAnalyses: async (bonusAnalyses, actionId) => {
    const { user } = useAuthStore.getState();
    if (!user?.id) return { data: null, error: 'Not authenticated' };

    try {
      const { data: currentSub, error: fetchError } = await supabase
        .from('user_analyses')
        .select('*')
        .eq('user_id', user.id)
        .single();
      if (fetchError) throw fetchError;

      const { data, error } = await supabase
        .from('user_analyses')
        .update({
          analyses_remaining: (currentSub?.analyses_remaining || 0) + bonusAnalyses,
          actions_completed: [...(currentSub?.actions_completed || []), actionId],
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;

      set({ subscription: data as Subscription });
      return { data: data as Subscription, error: null };
    } catch (error) {
      set({ error: (error as any)?.message || 'Bonus update failed' });
      return { data: null, error };
    }
  },
}));
